import { ComponentController as BpController } from '@rta/rta-blueprint-component';
import { BlueprintInstanceType, CornerSize, Island, Size } from '@rta/rta-blueprint-library';
import { Box, Divider, Text } from '@chakra-ui/react';
import { useEffect, useState, type JSX } from 'react';

import { ContextPanelContent } from './context-panel-content';
import { IslandSizeControls } from './island-properties/island-size-controls';
import { lang } from '@lang';
import { PanelThicknessControls } from './island-properties/panel-thickness-controls';
import { useDesign } from '@hooks';
import { usePanelThickness } from './island-properties/use-panel-thickness';

export type IslandPropertiesPanelProps = {
	id: string | null;
};

export const IslandPropertiesPanel = (props: IslandPropertiesPanelProps): JSX.Element => {
	const { id } = props;
	const [controller] = useDesign<BpController>();
	const [island, setIsland] = useState<Island | null>(null);
	const [standardSize, setStandardSize] = useState<Size>();
	const [cornerSize, setCornerSize] = useState<CornerSize>();

	const readonly = (controller?.readonly ?? true) || (island?.sizeLocked ?? true);
	const controllerReadonly = controller?.readonly ?? true;

	const thickness = usePanelThickness(island);

	useEffect(() => {
		if (controller) {
			let refMap = controller.kitchen?.getReferenceMap(id ?? '');
			if (refMap?.type === BlueprintInstanceType.Countertop && refMap.countertop?.islandIds?.[0]) {
				refMap = controller.kitchen?.getReferenceMap(refMap.countertop.islandIds[0]);
			}
			const resolvedIsland = refMap?.island ?? null;
			if (!resolvedIsland) return;
			setIsland(resolvedIsland);
			if (resolvedIsland.isCornerIsland) {
				setCornerSize(resolvedIsland.size as CornerSize);
			} else {
				setStandardSize(resolvedIsland.size as Size);
			}
			thickness.initFromIsland(resolvedIsland);
		}
	}, [controller, id]);

	if (!controller || !island || !(standardSize || cornerSize)) return <></>;

	const { kitchen } = controller;
	const islands = kitchen?.design.islands.children ?? [];

	return <ContextPanelContent title={lang.contextPanels.title.islandProperties}>
		<Box width="100%" mb="5px" mt="5px" padding="5px" textAlign="left" borderLeft="1px solid grey" borderBottom="1px solid darkgrey">
			<IslandSizeControls
				island={island}
				standardSize={standardSize}
				cornerSize={cornerSize}
				readonly={readonly}
				controllerReadonly={controllerReadonly}
				islandCount={islands.length}
				leftPanel={island.frontPanel.getLeftPanel()}
				rightPanel={island.frontPanel.getRightPanel()}
				onStandardSizeChange={setStandardSize}
				onCornerSizeChange={setCornerSize}
			/>
		</Box>
		<Box width="100%" padding="5px" textAlign="left">
			<Divider mb="5px" />
			<Text
				fontSize="small" fontWeight="bold" cursor="pointer" userSelect="none"
				onClick={() => thickness.setAdvancedOpen(!thickness.advancedOpen)}
			>
				{thickness.advancedOpen ? '\u25BC' : '\u25B6'} Panel Thickness
			</Text>
			{ thickness.advancedOpen &&
				<PanelThicknessControls
					island={island}
					readonly={controllerReadonly}
					uniform={thickness.uniform}
					thicknessValues={thickness.thicknessValues}
					onUniformToggle={thickness.handleUniformToggle}
					onUniformThicknessChange={thickness.handleUniformThicknessChange}
					onPanelThicknessChange={thickness.handlePanelThicknessChange}
					onResetToDefault={thickness.handleResetToDefault}
				/>
			}
		</Box>
	</ContextPanelContent>;
};
